import * as React from 'react';
import { useState } from 'react';
import dateUtil from '../../../utils/dateUtil';
import { Employee } from '../../../hooks/useEmployeeData';
import TimeTrackingService from '../services/TimeTrackingService';
import { HttpError } from '../../../core/services/HttpService';
import globalErrorHandler from '../../../core/services/GlobalErrorHandler';
import timeTrackingErrorHandler from '../services/TimeTrackingErrorHandler';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';
import { toast } from 'react-toastify';

interface WorkArea {
  id: number;
  name: string;
  [key: string]: any;
}

interface TTAddModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  employees: Employee[];
  workAreas: WorkArea[];
  activeEmployeeId?: number | null;
}

const addOneHour = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  return `${String((h + 1) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export default function TTAddModal({
  open,
  onClose,
  onSave,
  employees,
  workAreas,
  activeEmployeeId,
}: TTAddModalProps): React.JSX.Element | null {
  const [addData, setAddData] = useState({
    employee: activeEmployeeId ? String(activeEmployeeId) : '',
    work_area: '',
    date: '',
    start_time: '09:00',
    end_time: '17:00',
    break_start: '',
    break_end: ''
  });

  // Reset form when modal opens with new activeEmployeeId
  React.useEffect(() => {
    if (open) {
      setAddData({
        employee: activeEmployeeId ? String(activeEmployeeId) : '',
        work_area: '',
        date: '',
        start_time: '09:00',
        end_time: '17:00',
        break_start: '',
        break_end: ''
      });
    }
  }, [open, activeEmployeeId]);

  if (!open) return null;

  const glassInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '7px 10px',
    borderRadius: '6px',
    border: `1px solid ${colors.border}`,
    fontSize: '12px',
    background: colors.bgCard,
    color: colors.textPrimary,
    boxSizing: 'border-box',
  };

  const glassLabelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '4px',
    fontSize: '11px',
    fontWeight: 600,
    color: colors.textBody,
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value }= e.target;
    const update: any = { [name]: value };
    if (name === 'start_time' && value) update.end_time = addOneHour(value);
    if (name === 'break_start' && value) update.break_end = addOneHour(value);
    setAddData(prev => ({...prev, ...update}));
  };

  const handleSave = async () => {
    if (!addData.date || !addData.start_time || !addData.end_time) {
      toast.warning('Please fill out date & times');
      return;
    }
    const stIso= dateUtil.createTenantTimeAsUTC(addData.date, addData.start_time);
    const etIso= dateUtil.createTenantTimeAsUTC(addData.date, addData.end_time);

    let bsIso=null, beIso=null;
    if (addData.break_start) bsIso= dateUtil.createTenantTimeAsUTC(addData.date, addData.break_start);
    if (addData.break_end)   beIso= dateUtil.createTenantTimeAsUTC(addData.date, addData.break_end);

    const payload={
      employee: addData.employee,
      work_area: addData.work_area,
      start_time: stIso,
      end_time:   etIso,
      break_start: bsIso,
      break_end:   beIso
    };

    try {
      await TimeTrackingService.createTimeTracking(payload);
      onClose();
      onSave();
    } catch (error) {
      console.error('Add TT =>', error);

      if (error instanceof HttpError) {
        if (!timeTrackingErrorHandler.handle(error)) {
          globalErrorHandler.handle(error);
        }
      } else {
        toast.error('Fehler beim Erstellen der Zeiterfassung');
      }
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 5001,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.bgCard,
          borderRadius: '14px',
          padding: '16px',
          width: '420px',
          maxHeight: '90vh',
          overflowY: 'auto',
          border: `1px solid ${colors.border}`,
          boxShadow: shadows.elevated,
        }}
        data-testid="add-tt-modal"
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: colors.slate }}>
            New TimeTracking
          </h2>
          <button
            onClick={onClose}
            style={{
              background: colors.bgMuted,
              border: 'none',
              borderRadius: '6px',
              width: '26px',
              height: '26px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: colors.textMuted,
              fontSize: '12px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {/* Employee & Work Area */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>
              <label style={glassLabelStyle}>Employee</label>
              <select
                name="employee"
                value={addData.employee}
                onChange={handleChange}
                style={glassInputStyle}
                data-testid="add-tt-employee-select"
              >
                <option value="">(choose)</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={glassLabelStyle}>Work Area</label>
              <select
                name="work_area"
                value={addData.work_area}
                onChange={handleChange}
                style={glassInputStyle}
                data-testid="add-tt-workarea-select"
              >
                <option value="">(none)</option>
                {workAreas.map(wa => (
                  <option key={wa.id} value={wa.id}>
                    {wa.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Date */}
          <div>
            <label style={glassLabelStyle}>Date</label>
            <input
              type="date"
              name="date"
              value={addData.date}
              onChange={handleChange}
              style={glassInputStyle}
              data-testid="add-tt-date-input"
            />
          </div>

          {/* Start/End Time */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>
              <label style={glassLabelStyle}>Start Time</label>
              <input
                type="time"
                name="start_time"
                value={addData.start_time}
                onChange={handleChange}
                style={glassInputStyle}
                data-testid="add-tt-start-time-input"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={glassLabelStyle}>End Time</label>
              <input
                type="time"
                name="end_time"
                value={addData.end_time}
                onChange={handleChange}
                style={glassInputStyle}
                data-testid="add-tt-end-time-input"
              />
            </div>
          </div>

          {/* Break Times */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>
              <label style={glassLabelStyle}>Break Start</label>
              <input
                type="time"
                name="break_start"
                value={addData.break_start}
                onChange={handleChange}
                style={glassInputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={glassLabelStyle}>Break End</label>
              <input
                type="time"
                name="break_end"
                value={addData.break_end}
                onChange={handleChange}
                style={glassInputStyle}
              />
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: `1px solid ${colors.border}`,
              background: colors.bgCard,
              color: colors.textMuted,
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              ...ps.buttonPrimary,
              padding: '8px 16px',
              fontSize: '12px',
            }}
            data-testid="add-tt-save-button"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
